import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message, Typography, Space } from 'antd';
import { useDispatch } from 'react-redux';
import { handleSend2FACode, handleVerify2FACode } from 'store/permissions/thunks';
import Loading from 'components/Loading';
import styled from 'styled-components';
import SentryWrap from 'components/Sentry';
const { Text, Title } = Typography;

const CodeInput = styled(Input)`
    font-size: 24px;
    text-align: center;
    letter-spacing: 8px;
    font-weight: bold;
    height: 60px;
`;

const ModalContent = styled.div`
    padding: 20px 0;
    text-align: center;
`;

const TwoFactorAuthModal = ({ visible, user, onVerify, onCancel }) => {
    const dispatch = useDispatch();
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        if (visible && user && user.email) {
            sendCode();
        }
    }, [visible, user]);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [countdown]);

    const sendCode = async () => {
        if (!user || !user.email || !user.id) {
            message.error('User information is missing');
            return;
        }

        setSendingCode(true);
        setCodeSent(false);
        try {
            const response = await dispatch(handleSend2FACode(user.id, user.email, user.username));
            if (response && response.success) {
                message.success('Verification code sent to your email');
                setCodeSent(true);
                setCountdown(60);
            } else {
                message.error(response?.error?.message || 'Failed to send verification code');
            }
        } catch (error) {
            message.error('Failed to send verification code');
        } finally {
            setSendingCode(false);
        }
    };

    const handleVerify = async () => {
        if (!code || code.length !== 6) {
            message.error('Please enter a valid 6-digit code');
            return;
        }

        setLoading(true);
        try {
            const response = await dispatch(handleVerify2FACode(user.id, code));
            if (response && response.success) {
                message.success('Verification successful');
                onVerify();
            } else {
                message.error(response?.error?.message || 'Invalid verification code');
                setCode('');
            }
        } catch (error) {
            message.error('Failed to verify code');
            setCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setCode(value);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && code.length === 6) {
            handleVerify();
        }
    };

    const handleClose = () => {
        setCode('');
        setCodeSent(false);
        setCountdown(0);
        onCancel();
    };

    return (
    <SentryWrap>
        <Modal
            title={
                <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
                    Two-Factor Authentication
                </Title>
            }
            open={visible}
            onCancel={handleClose}
            footer={null}
            closable={!loading}
            maskClosable={false}
            width={500}
        >
            <ModalContent>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Text>
                            A verification code has been sent to <strong>{user?.email}</strong>
                        </Text>
                        {codeSent && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Please check your email and enter the 6-digit code below.
                                </Text>
                            </div>
                        )}
                    </div>

                    <div>
                        <CodeInput
                            value={code}
                            onChange={handleCodeChange}
                            onKeyPress={handleKeyPress}
                            placeholder="000000"
                            maxLength={6}
                            disabled={loading || !codeSent}
                            autoFocus
                        />
                    </div>

                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            onClick={handleVerify}
                            loading={loading}
                            disabled={code.length !== 6 || !codeSent}
                            block
                            size="large"
                        >
                            {loading ? <Loading size={20} white /> : 'Verify Code'}
                        </Button>

                        <Button
                            onClick={sendCode}
                            loading={sendingCode}
                            disabled={countdown > 0 || sendingCode}
                            block
                        >
                            {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend Code'}
                        </Button>
                    </Space>
                </Space>
            </ModalContent>
        </Modal>
    </SentryWrap>
    );
};

export default TwoFactorAuthModal;

